import { useQuery, useQueries } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { STATUS_GROUPS } from "@finitless/shared/orderConstants";

// Build where clause for user-specific orders
const buildUserOrdersWhereClause = (statusFilter, searchTerm, userId) => {
  const conditions = [{ attribute: "customer_id", value: userId }];

  // Add status filter if not 'all'
  if (statusFilter !== "all") {
    let statusValues = [];

    switch (statusFilter) {
      case "active":
        statusValues = [...STATUS_GROUPS.pending, ...STATUS_GROUPS.inProgress];
        break;
      case "completed":
        statusValues = STATUS_GROUPS.completed;
        break;
      case "cancelled":
        statusValues = STATUS_GROUPS.cancelled;
        break;
      default:
        statusValues = [];
    }

    if (statusValues.length > 0) {
      conditions.push({ attribute: "status", value: statusValues });
    }
  }

  // Add search term for order ID if provided
  if (searchTerm && searchTerm.trim()) {
    const searchValue = `%${searchTerm.trim()}%`;
    conditions.push({
      conector: "OR",
      conditions: [
        {
          attribute: "id",
          value: {
            condition: "ilike",
            value: searchValue,
          },
        },
      ],
    });
  }

  return {
    conditions,
    conector: "AND",
  };
};

const useUserOrders = ({
  userId,
  statusFilter = "all",
  searchTerm = "",
  currentPage = 1,
  itemsPerPage = 10,
}) => {
  const whereClause = buildUserOrdersWhereClause(
    statusFilter,
    searchTerm,
    userId,
  );
  const queryString = new URLSearchParams({
    where: JSON.stringify(whereClause),
    page: currentPage,
    page_size: itemsPerPage,
    orderBy: "-id",
    mode: "dashboard",
  }).toString();

  return useQuery({
    queryKey: ["user-orders", userId, statusFilter, searchTerm, currentPage],
    queryFn: async () => {
      const response = await apiClient.get(`/orders?${queryString}`);
      const data = response;

      // Handle API response structure
      if (data.error) {
        throw new Error(data.message || "Error fetching orders");
      }

      return {
        orders: data.result || [],
        pagination: data.pagination || {
          total: 0,
          current_page: 1,
          total_pages: 10,
          page_size: itemsPerPage,
        },
      };
    },
    enabled: !!userId,
    staleTime: 300000, // 5 minutes
    // retry: 2
  });
};

// Hook for getting order counts for all tabs (always fetches all to cache them)
const useUserOrdersStats = ({ userId, searchTerm = "" }) => {
  // Constants for query parameters
  const QUERY_PAGE = 1;
  const QUERY_PAGE_SIZE = 10;
  const QUERY_ORDER_BY = "-id";
  const QUERY_MODE = "dashboard";
  const QUERY_STALE_TIME = 300000; // 5 minutes - longer cache
  const QUERY_PARAMS =
    "id,external_id,conversation_id,status,products,summary,customer,delivery_datetime_utc,created_at,taxes";

  const queries = useQueries({
    queries: [
      {
        queryKey: ["user-orders", userId, "active", searchTerm, QUERY_PAGE],
        queryFn: async () => {
          const whereClause = buildUserOrdersWhereClause(
            "active",
            searchTerm,
            userId,
          );
          const queryString = new URLSearchParams({
            params: QUERY_PARAMS,
            where: JSON.stringify(whereClause),
            page: QUERY_PAGE,
            page_size: QUERY_PAGE_SIZE,
            orderBy: QUERY_ORDER_BY,
            mode: QUERY_MODE,
          }).toString();

          const response = await apiClient.get(`/orders?${queryString}`);
          const data = response;

          if (data.error) {
            throw new Error(data.message || "Error fetching orders");
          }

          return {
            orders: data.result || [],
            pagination: data.pagination || {
              total: 0,
              current_page: QUERY_PAGE,
              total_pages: 1,
              page_size: QUERY_PAGE_SIZE,
            },
          };
        },
        enabled: !!userId,
        staleTime: QUERY_STALE_TIME,
      },
      {
        queryKey: ["user-orders", userId, "completed", searchTerm, QUERY_PAGE],
        queryFn: async () => {
          const whereClause = buildUserOrdersWhereClause(
            "completed",
            searchTerm,
            userId,
          );
          const queryString = new URLSearchParams({
            params: QUERY_PARAMS,
            where: JSON.stringify(whereClause),
            page: QUERY_PAGE,
            page_size: QUERY_PAGE_SIZE,
            orderBy: QUERY_ORDER_BY,
            mode: QUERY_MODE,
          }).toString();

          const response = await apiClient.get(`/orders?${queryString}`);
          const data = response;

          if (data.error) {
            throw new Error(data.message || "Error fetching orders");
          }

          return {
            orders: data.result || [],
            pagination: data.pagination || {
              total: 0,
              current_page: QUERY_PAGE,
              total_pages: 1,
              page_size: QUERY_PAGE_SIZE,
            },
          };
        },
        enabled: !!userId,
        staleTime: QUERY_STALE_TIME,
      },
      {
        queryKey: ["user-orders", userId, "cancelled", searchTerm, QUERY_PAGE],
        queryFn: async () => {
          const whereClause = buildUserOrdersWhereClause(
            "cancelled",
            searchTerm,
            userId,
          );
          const queryString = new URLSearchParams({
            params: QUERY_PARAMS,
            where: JSON.stringify(whereClause),
            page: QUERY_PAGE,
            page_size: QUERY_PAGE_SIZE,
            orderBy: QUERY_ORDER_BY,
            mode: QUERY_MODE,
          }).toString();

          const response = await apiClient.get(`/orders?${queryString}`);
          const data = response;

          if (data.error) {
            throw new Error(data.message || "Error fetching orders");
          }

          return {
            orders: data.result || [],
            pagination: data.pagination || {
              total: 0,
              current_page: QUERY_PAGE,
              total_pages: 1,
              page_size: QUERY_PAGE_SIZE,
            },
          };
        },
        enabled: !!userId,
        staleTime: QUERY_STALE_TIME,
      },
    ],
  });

  return {
    active: queries[0]?.data?.pagination?.total || 0,
    completed: queries[1]?.data?.pagination?.total || 0,
    cancelled: queries[2]?.data?.pagination?.total || 0,
    isLoading: queries.some((query) => query.isLoading),
    error: queries.find((query) => query.error)?.error || null,
    // Return the data for the active tab to reuse
    activeData: queries[0]?.data,
    completedData: queries[1]?.data,
    cancelledData: queries[2]?.data,
  };
};

export { useUserOrders, useUserOrdersStats };
